/* Views dropdown — lives in the cockpit header next to RESET LAYOUT.
   Click opens a list of saved views (builtins + user-saved), plus a "save
   current as…" prompt. ESC or click-outside closes. */

import { useCallback, useEffect, useRef, useState } from "react"
import type { Layout } from "react-grid-layout/legacy"
import type { SavedView, UseViewsReturn } from "./useViews"

interface ViewsMenuProps {
  views: UseViewsReturn["views"]
  activeViewId: UseViewsReturn["activeViewId"]
  saveView: UseViewsReturn["saveView"]
  loadView: UseViewsReturn["loadView"]
  deleteView: UseViewsReturn["deleteView"]
  currentLayout: Layout
  onLoadLayout: (view: SavedView) => void
}

export function ViewsMenu({
  views,
  activeViewId,
  saveView,
  loadView,
  deleteView,
  currentLayout,
  onLoadLayout,
}: ViewsMenuProps) {
  const [open, setOpen] = useState(false)
  const [showSaveInput, setShowSaveInput] = useState(false)
  const [draftName, setDraftName] = useState("")
  const containerRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const activeView = views.find((v) => v.id === activeViewId) ?? views[0]
  const activeLabel = activeView?.name ?? "Default"

  const close = useCallback(() => {
    setOpen(false)
    setShowSaveInput(false)
    setDraftName("")
  }, [])

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    const onDocMouseDown = (e: MouseEvent) => {
      const el = containerRef.current
      if (el && e.target instanceof Node && !el.contains(e.target)) close()
    }
    document.addEventListener("mousedown", onDocMouseDown)
    return () => document.removeEventListener("mousedown", onDocMouseDown)
  }, [open, close])

  // Close on ESC (global, scoped to when open).
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close()
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [open, close])

  // Auto-focus the save input when shown.
  useEffect(() => {
    if (showSaveInput) inputRef.current?.focus()
  }, [showSaveInput])

  const handleSelect = useCallback(
    (view: SavedView) => {
      const loaded = loadView(view.id)
      if (loaded) onLoadLayout(loaded)
      close()
    },
    [loadView, onLoadLayout, close],
  )

  const handleDelete = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>, view: SavedView) => {
      e.stopPropagation()
      if (view.builtin) return
      deleteView(view.id)
    },
    [deleteView],
  )

  const commitSave = useCallback(() => {
    const name = draftName.trim()
    if (!name) {
      setShowSaveInput(false)
      setDraftName("")
      return
    }
    const saved = saveView(name, currentLayout)
    if (saved) onLoadLayout(saved)
    close()
  }, [draftName, saveView, currentLayout, onLoadLayout, close])

  const handleSaveKey = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault()
        commitSave()
      } else if (e.key === "Escape") {
        e.preventDefault()
        // Cancel just the save input; keep the menu itself open.
        setShowSaveInput(false)
        setDraftName("")
      }
    },
    [commitSave],
  )

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className="btn-pill flex items-center gap-1.5"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        title="Saved layout views"
      >
        <span>{activeLabel}</span>
        <span aria-hidden="true" className="text-[9px] leading-none">
          &#9662;
        </span>
      </button>

      {open && (
        <div
          role="menu"
          className="ui-panel--glass absolute right-0 top-[110%] z-50 w-64 p-1 shadow-lg"
          style={{ borderRadius: 4 }}
        >
          <div className="category-heading">Views</div>
          <ul className="flex flex-col">
            {views.map((view) => {
              const isActive = view.id === activeViewId
              return (
                <li key={view.id}>
                  <button
                    type="button"
                    role="menuitem"
                    onClick={() => handleSelect(view)}
                    className="flex w-full items-center gap-2 rounded-[3px] px-2 py-1.5 text-left text-[12px] text-[color:var(--text)] hover:bg-[color:var(--surface-hover)]"
                    style={
                      isActive
                        ? { color: "var(--accent)", background: "rgba(34, 211, 238, 0.08)" }
                        : undefined
                    }
                  >
                    <span className="flex-1 truncate">{view.name}</span>
                    {view.builtin ? (
                      <span className="text-[9px] uppercase tracking-wider text-[color:var(--text-dim)]">
                        builtin
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => handleDelete(e, view)}
                        title="Delete view"
                        aria-label={`Delete view ${view.name}`}
                        className="rounded-sm px-1 text-[14px] leading-none text-[color:var(--text-dim)] hover:text-[color:var(--sev-critical)]"
                      >
                        &times;
                      </button>
                    )}
                  </button>
                </li>
              )
            })}
          </ul>

          <div className="my-1 border-t border-[color:var(--border)]" />

          {showSaveInput ? (
            <div className="flex items-center gap-1 p-1">
              <input
                ref={inputRef}
                type="text"
                className="search-input flex-1"
                placeholder="View name…"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={handleSaveKey}
                maxLength={48}
              />
              <button
                type="button"
                className="btn-pill"
                onClick={commitSave}
                disabled={!draftName.trim()}
                style={!draftName.trim() ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
              >
                save
              </button>
            </div>
          ) : (
            <button
              type="button"
              role="menuitem"
              onClick={() => setShowSaveInput(true)}
              className="flex w-full items-center gap-2 rounded-[3px] px-2 py-1.5 text-left text-[12px] text-[color:var(--text-muted)] hover:bg-[color:var(--surface-hover)] hover:text-[color:var(--text)]"
            >
              <span aria-hidden="true">+</span>
              <span>Save current as&hellip;</span>
            </button>
          )}
        </div>
      )}
    </div>
  )
}
